import apiClient from './apiClient';
import type {
  DashboardResponse,
  DashboardSummary,
  DashboardOperations,
  QueueCharts
} from '../types';

/**
 * Service para interagir com a API do Dashboard
 *
 * O Dashboard tem 3 partes:
 * - Parte 1: Summary - Totais de entidades (atualizado diariamente às 2AM)
 * - Parte 2: Operations - Métricas de operações das últimas 24h (atualizado de hora em hora)
 * - Parte 3: Queue Charts - Gráficos de erros/agendamentos por hora (atualizado de hora em hora)
 */
export const dashboardService = {
  /**
   * Obtém os dados completos do dashboard (todas as 3 partes)
   */
  async getDashboard(): Promise<DashboardResponse> {
    const response = await apiClient.get<DashboardResponse>('/dashboard');
    return response.data;
  },

  /**
   * Obtém apenas os totais de entidades (Parte 1)
   * Dados vindos do Graph API, atualizados diariamente às 2AM
   */
  async getSummary(): Promise<DashboardSummary> {
    const response = await apiClient.get<DashboardSummary>('/dashboard/summary');
    return response.data;
  },

  /**
   * Obtém apenas as métricas de operações (Parte 2)
   * Dados das últimas 24h, atualizados de hora em hora
   */
  async getOperations(): Promise<DashboardOperations> {
    const response = await apiClient.get<DashboardOperations>('/dashboard/operations');
    return response.data;
  },

  /**
   * Obtém apenas os dados dos gráficos de filas (Parte 3)
   * Histórico de 24h com dados por hora
   */
  async getQueueCharts(): Promise<QueueCharts> {
    const response = await apiClient.get<QueueCharts>('/dashboard/queue-charts');
    return response.data;
  },

  /**
   * Formata número com separador de milhares
   */
  formatNumber(value: number): string {
    return value.toLocaleString('pt-BR');
  },

  /**
   * Formata data para exibição
   */
  formatDate(dateString: string): string {
    if (!dateString || dateString === '0001-01-01T00:00:00') {
      return 'Nunca atualizado';
    }
    return new Date(dateString).toLocaleString('pt-BR');
  },

  /**
   * Calcula a variação percentual entre dois valores
   */
  calculateVariation(current: number, previous: number): {
    percentage: string;
    trend: 'up' | 'down' | 'stable';
  } {
    if (previous === 0) {
      return { percentage: current > 0 ? '+100%' : '0%', trend: current > 0 ? 'up' : 'stable' };
    }

    const variation = ((current - previous) / previous) * 100;

    if (Math.abs(variation) < 1) {
      return { percentage: '0%', trend: 'stable' };
    }

    return {
      percentage: `${variation > 0 ? '+' : ''}${variation.toFixed(1)}%`,
      trend: variation > 0 ? 'up' : 'down'
    };
  }
};
